import type {
    AftermarketCertification,
    InsuranceDrpSettings,
    PartType,
} from '../../hooks/useGetSettings';

/** A single price-match card as it is stored on the settings document. */
export type PriceMatchEntry = NonNullable<InsuranceDrpSettings['priceMatchCustomRanking']>[number];

export type PriceMatchSource = 'partstrader';

/** Editable copy of a card, with a stable id so it can be dragged. */
export interface PriceMatchDraftEntry {
    id: string;
    partType?: PartType;
    aftermarketCertifications?: Array<AftermarketCertification>;
    supplierId?: string;
    supplierName?: string;
    source: Array<PriceMatchSource>;
}

/** How many cards one order can hold. */
export const MAX_PRICE_MATCH_ENTRIES = 10;

/**
 * OEM is deliberately absent: the order ranks the alternatives PartBay price
 * matches an OEM part against, so an OEM card would match everything.
 */
export const PART_TYPE_OPTIONS: Array<{ value: PartType; label: string }> = [
    { value: 'oe-surplus', label: 'OE Surplus' },
    { value: 'a/m', label: 'Aftermarket' },
    { value: 'rcy', label: 'Recycled' },
    { value: 're-man', label: 'Remanufactured' },
    { value: 're-con', label: 'Reconditioned' },
];

export const AFTERMARKET_CERT_OPTIONS: Array<{ value: AftermarketCertification; label: string }> = [
    { value: 'nsf', label: 'NSF' },
    { value: 'capa', label: 'CAPA' },
    { value: 'ds', label: 'DS' },
    { value: 'pp', label: 'PP' },
    { value: 'keysiq', label: 'KeysIQ' },
];

export const MARKETPLACE_OPTIONS: Array<{ value: PriceMatchSource; label: string }> = [
    { value: 'partstrader', label: 'PartsTrader' },
];

export const PART_TYPE_LABEL = Object.fromEntries(
    PART_TYPE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<PartType, string>;

export const CERT_LABEL = Object.fromEntries(
    AFTERMARKET_CERT_OPTIONS.map((option) => [option.value, option.label]),
) as Record<AftermarketCertification, string>;

export const MARKETPLACE_LABEL = Object.fromEntries(
    MARKETPLACE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<PriceMatchSource, string>;

/** Ids only ever live in the browser, so the clock plus noise is enough. */
export const makePriceMatchId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const emptyPriceMatchDraft = (): PriceMatchDraftEntry => ({
    id: makePriceMatchId(),
    source: [],
});

export const toPriceMatchDraft = (entry: PriceMatchEntry): PriceMatchDraftEntry => ({
    id: makePriceMatchId(),
    partType: entry.partType,
    aftermarketCertifications: entry.aftermarketCertifications,
    supplierId: entry.supplierId,
    supplierName: entry.supplierName,
    source: (entry.source ?? []).filter((source): source is PriceMatchSource => source === 'partstrader'),
});

/** Strips the local id and returns the stored shape. */
export const fromPriceMatchDraft = (draft: PriceMatchDraftEntry): PriceMatchEntry => {
    const entry: Record<string, unknown> = { source: draft.source };
    if (draft.partType) {
        entry.partType = draft.partType;
        // Certifications only mean anything on an aftermarket card.
        if (draft.partType === 'a/m' && draft.aftermarketCertifications?.length) {
            entry.aftermarketCertifications = draft.aftermarketCertifications;
        }
    }
    if (draft.supplierId) {
        entry.supplierId = draft.supplierId;
        entry.supplierName = draft.supplierName;
    }
    return entry as unknown as PriceMatchEntry;
};

/** One criterion is enough for a card: the rest is left wide open. */
export const isPriceMatchDraftValid = (draft: PriceMatchDraftEntry): boolean =>
    !!draft.partType || !!draft.supplierId || draft.source.length > 0;

type PriceMatchCriteria = {
    partType?: PartType;
    aftermarketCertifications?: Array<AftermarketCertification>;
    supplierName?: string;
    source?: Array<PriceMatchSource>;
};

/** Joins a list the way a sentence would: "A, B and C". */
const joinWords = (words: Array<string>, conjunction = 'and'): string => {
    if (words.length <= 1) return words[0] ?? '';
    return `${words.slice(0, -1).join(', ')} ${conjunction} ${words[words.length - 1]}`;
};

/** Compact, label-style summary of a card, for a settings row or a chip line. */
export const summarizePriceMatchEntry = (entry: PriceMatchCriteria): string => {
    const parts: Array<string> = [];
    if (entry.supplierName) parts.push(`Vendor: ${entry.supplierName}`);
    if (entry.partType) {
        const certs =
            entry.partType === 'a/m' && entry.aftermarketCertifications?.length
                ? ` (${entry.aftermarketCertifications.map((cert) => CERT_LABEL[cert]).join(', ')})`
                : '';
        parts.push(`${PART_TYPE_LABEL[entry.partType]}${certs}`);
    }
    if (entry.source?.length) {
        parts.push(`Source: ${entry.source.map((source) => MARKETPLACE_LABEL[source]).join(', ')}`);
    }
    return parts.length ? parts.join('  ·  ') : 'Empty card';
};

/**
 * The card read back as a sentence, so what a set of criteria actually does is
 * legible before it is added. Every clause is optional — only the criteria the
 * user picked appear.
 */
export const describePriceMatchEntry = (entry: PriceMatchCriteria): string => {
    const vendor = entry.supplierName ? `${entry.supplierName} ` : '';
    const clauses: Array<string> = [];

    if (entry.partType) {
        const certs =
            entry.partType === 'a/m' && entry.aftermarketCertifications?.length
                ? ` and ${joinWords(
                      entry.aftermarketCertifications.map((cert) => CERT_LABEL[cert]),
                      'or',
                  )} certified`
                : '';
        clauses.push(` when they are ${PART_TYPE_LABEL[entry.partType]}${certs}`);
    }
    if (entry.source?.length) {
        clauses.push(
            ` using the prices from ${joinWords(
                entry.source.map((source) => MARKETPLACE_LABEL[source]),
            )}`,
        );
    }

    return `We'll price match to ${vendor}parts${clauses.join('')}.`;
};

/** The criteria on a card, as short chips for the collapsed view. */
export const priceMatchEntryChips = (
    entry: PriceMatchCriteria,
): Array<{ label: string; colorScheme: string }> => {
    const chips: Array<{ label: string; colorScheme: string }> = [];
    if (entry.supplierName) chips.push({ label: entry.supplierName, colorScheme: 'blue' });
    if (entry.partType) chips.push({ label: PART_TYPE_LABEL[entry.partType], colorScheme: 'green' });
    if (entry.partType === 'a/m') {
        (entry.aftermarketCertifications ?? []).forEach((cert) =>
            chips.push({ label: CERT_LABEL[cert], colorScheme: 'purple' }),
        );
    }
    (entry.source ?? []).forEach((source) =>
        chips.push({ label: MARKETPLACE_LABEL[source], colorScheme: 'orange' }),
    );
    return chips;
};
